import React, { useEffect, useState, useCallback } from 'react';
import { getTeamStatistics, TeamStatistics as TeamStatsData } from '../../services/statisticsService';
import { formatDistance, formatTime, formatPace, formatSpeed, formatDate, formatElevation } from '../../utils/formatters';
import { FaUsers, FaRunning, FaRoad, FaClock, FaMedal, FaSpinner, FaExclamationTriangle, FaSearch, FaRedo, FaBuilding, FaMountain, FaHourglassHalf, FaFilter, FaSortAmountDown, FaSortAmountUp, FaTimes } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import './statistics.css';
import UserActivitiesDialog from '../../components/UserActivitiesDialog';
import { debounce } from 'lodash';

interface TeamStatisticsProps {
  teamId: number;
  startDateLocal: string;
  endDate: string;
}

type SortField = 'fullName' | 'totalDistance' | 'activityCount' | 'totalMovingTime' | 'averagePace' | 'lastActivityDate';
type SortDirection = 'asc' | 'desc';
type FilterOption = 'all' | 'active' | 'inactive' | 'department';

const TeamStatistics: React.FC<TeamStatisticsProps> = ({ teamId, startDateLocal, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [teamStats, setTeamStats] = useState<TeamStatsData | null>(null);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [tableHeight, setTableHeight] = useState<number | null>(null);
  
  // Log để kiểm tra giá trị startDateLocal và endDate
  console.log("TeamStatistics - Khoảng thời gian:", { startDateLocal, endDate });
  
  // Trạng thái cho bộ lọc và sắp xếp
  const [sortField, setSortField] = useState<SortField>('totalDistance');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterType, setFilterType] = useState<FilterOption>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  
  // Danh sách phòng ban duy nhất từ dữ liệu thành viên
  const [departments, setDepartments] = useState<string[]>([]);

  // Sử dụng debounce để trì hoãn cập nhật debouncedSearch
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300),
    []
  );

  // Cập nhật giá trị search và gọi debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    debouncedSetSearch(e.target.value);
  };

  useEffect(() => {
    const fetchTeamStatistics = async () => {
      try {
        setLoading(true);
        console.log(`Đang gọi API thống kê cho đội ID: ${teamId}, từ ngày: ${startDateLocal}, đến ngày: ${endDate}`);
        const data = await getTeamStatistics(teamId, startDateLocal, endDate);
        console.log(`Đã nhận dữ liệu thống kê cho đội ID: ${teamId}`, data);
        setTeamStats(data);
        
        // Lấy danh sách phòng ban duy nhất
        if (data?.memberStats) {
          const deptList = data.memberStats
            .map(member => member.department)
            .filter((dept): dept is string => !!dept)
            .filter((dept, index, self) => self.indexOf(dept) === index);
          setDepartments(deptList);
        }
        
        setError(null);
      } catch (err: any) {
        console.error(`Lỗi khi lấy thống kê đội ID: ${teamId}:`, err);
        setError(err.message || 'Không thể lấy thông tin thống kê đội');
      } finally {
        setLoading(false);
      }
    };

    if (teamId && teamId > 0) {
      fetchTeamStatistics();
    } else {
      console.warn('Không thể lấy thống kê đội: teamId không hợp lệ', teamId);
    }
  }, [teamId, startDateLocal, endDate]);

  // Lưu chiều cao của bảng sau khi dữ liệu được tải
  useEffect(() => {
    if (teamStats?.memberStats && teamStats.memberStats.length > 0) {
      const tableElement = document.querySelector('.ranking-table');
      if (tableElement) {
        setTableHeight(tableElement.clientHeight);
      }
    }
  }, [teamStats]);
  
  const handleOpenUserActivities = (userId: number, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };
  
  // Hàm xử lý sắp xếp
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Nếu đang sắp xếp theo field này rồi, đổi hướng sắp xếp
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nếu chọn field mới, mặc định sắp xếp giảm dần
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Reset tất cả bộ lọc
  const resetFilters = () => {
    setFilterType('all');
    setSelectedDepartment('');
    setSearch('');
    setDebouncedSearch('');
    setSortField('totalDistance');
    setSortDirection('desc');
  };
  
  // Hàm để hiển thị mũi tên sắp xếp
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <FaSortAmountUp className="sort-icon" /> 
      : <FaSortAmountDown className="sort-icon" />;
  };

  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <FaExclamationTriangle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  if (!teamStats) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <FaUsers />
        </div>
        <div className="empty-state-text">Không có dữ liệu thống kê cho đội</div>
      </div>
    );
  }

  // Lọc và sắp xếp thành viên
  let filteredMembers = teamStats.memberStats ? [...teamStats.memberStats] : [];
  
  // Áp dụng bộ lọc tìm kiếm
  if (debouncedSearch) {
    filteredMembers = filteredMembers.filter(member =>
      member.fullName?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }
  
  // Áp dụng bộ lọc theo loại
  if (filterType === 'active') {
    filteredMembers = filteredMembers.filter(member => member.activityCount > 0);
  } else if (filterType === 'inactive') {
    filteredMembers = filteredMembers.filter(member => member.activityCount === 0);
  } else if (filterType === 'department' && selectedDepartment) {
    filteredMembers = filteredMembers.filter(member => member.department === selectedDepartment);
  }
  
  // Sắp xếp thành viên
  filteredMembers.sort((a, b) => {
    let valueA: any = a[sortField];
    let valueB: any = b[sortField];
    
    // Xử lý các trường hợp đặc biệt
    if (sortField === 'lastActivityDate') {
      // So sánh ngày (có thể null)
      if (!valueA) return sortDirection === 'asc' ? -1 : 1;
      if (!valueB) return sortDirection === 'asc' ? 1 : -1;
      
      // Nếu cả hai đều là mảng, so sánh từng phần tử
      if (Array.isArray(valueA) && Array.isArray(valueB)) {
        for (let i = 0; i < Math.min(valueA.length, valueB.length); i++) {
          if (valueA[i] !== valueB[i]) {
            return sortDirection === 'asc' 
              ? valueA[i] - valueB[i] 
              : valueB[i] - valueA[i];
          }
        }
        return 0;
      }
    }
    
    // So sánh chuỗi
    if (sortField === 'fullName') {
      valueA = valueA || '';
      valueB = valueB || '';
      return sortDirection === 'asc' 
        ? valueA.localeCompare(valueB) 
        : valueB.localeCompare(valueA);
    }
    
    // So sánh số
    if (valueA === null || valueA === undefined) valueA = 0;
    if (valueB === null || valueB === undefined) valueB = 0;
    
    return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
  });

  return (
    <div className={`team-statistics ${theme}`}>
      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <div className="statistics-card-title">
            <FaUsers /> {teamStats ? teamStats.teamName : 'Thống kê đội'}
          </div>
        </div>
        
        <div className="statistics-metrics">
          <div className="metric-card">
            <div className="metric-value">{teamStats.userCount}</div>
            <div className="metric-label">Thành viên</div>
          </div>
          
          <div className="metric-card">
            <div className="metric-value">{teamStats.activityCount}</div>
            <div className="metric-label">Hoạt động</div>
          </div>
          
          <div className="metric-card">
            <div className="metric-value">{formatDistance(teamStats.totalDistance)}</div>
            <div className="metric-label">Tổng quãng đường</div>
          </div>
          
          <div className="metric-card">
            <div className="metric-value">{formatTime(teamStats.totalMovingTime)}</div>
            <div className="metric-label">Thời gian chạy</div>
          </div>

          <div className="metric-card">
            <div className="metric-value">{formatTime(teamStats.totalElapsedTime)}</div>
            <div className="metric-label">Tổng thời gian</div>
          </div>
        </div>
      </div>

      {teamStats.topUser && (
        <div className="statistics-card">
          <div className="statistics-card-header">
            <h2 className="statistics-card-title">
              <FaMedal /> Thành viên xuất sắc nhất
            </h2>
          </div>
          
          <div className="top-user-card">
            {teamStats.topUser.avatarUrl ? (
              <img 
                src={teamStats.topUser.avatarUrl} 
                alt={teamStats.topUser.fullName}
                className="top-user-avatar"
              />
            ) : (
              <div className="top-user-avatar-placeholder">
                {teamStats.topUser.fullName.charAt(0).toUpperCase()}
              </div>
            )}
            
            <div className="top-user-info">
              <div className="top-user-name">{teamStats.topUser.fullName}</div>
              {teamStats.topUser.department && (
                <div className="top-user-department">
                  <FaBuilding size={14} /> {teamStats.topUser.department}
                </div>
              )}
              
              <div className="top-user-stats">
                <div>
                  <div className="metric-value">{formatDistance(teamStats.topUser.totalDistance)}</div>
                  <div className="metric-label">Quãng đường</div>
                </div>
                
                <div>
                  <div className="metric-value">{teamStats.topUser.activityCount}</div>
                  <div className="metric-label">Hoạt động</div>
                </div>
                
                <div>
                  <div className="metric-value">
                    {teamStats.topUser.averagePace ? formatPace(teamStats.topUser.averagePace) : '--'}
                  </div>
                  <div className="metric-label">Nhịp độ TB</div>
                </div>

                <div>
                  <div className="metric-value">{formatElevation(teamStats.topUser.totalElevationGain)}</div>
                  <div className="metric-label">Độ cao</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {teamStats.memberStats && teamStats.memberStats.length > 0 && (
        <div className="statistics-card">
          <div className="statistics-card-header">
            <h2 className="statistics-card-title">
              <FaRunning /> Thống kê thành viên
            </h2>
            <div className="filter-toggle" onClick={() => setIsFilterOpen(!isFilterOpen)}>
              <FaFilter /> Bộ lọc {isFilterOpen ? '▲' : '▼'}
            </div>
          </div>
          
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-section">
                <div className="filter-label">Hiển thị:</div>
                <div className="filter-options">
                  <button 
                    className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                    onClick={() => setFilterType('all')}
                  >
                    Tất cả
                  </button>
                  <button 
                    className={`filter-btn ${filterType === 'active' ? 'active' : ''}`}
                    onClick={() => setFilterType('active')}
                  >
                    Đã hoạt động
                  </button>
                  <button 
                    className={`filter-btn ${filterType === 'inactive' ? 'active' : ''}`}
                    onClick={() => setFilterType('inactive')}
                  >
                    Chưa hoạt động
                  </button>
                </div>
              </div>
              
              {departments.length > 0 && (
                <div className="filter-section">
                  <div className="filter-label">Phòng ban:</div>
                  <div className="filter-options">
                    <select 
                      className="department-select"
                      value={selectedDepartment}
                      onChange={(e) => {
                        setSelectedDepartment(e.target.value);
                        if (e.target.value) {
                          setFilterType('department');
                        } else if (filterType === 'department') {
                          setFilterType('all');
                        }
                      }}
                    >
                      <option value="">Tất cả phòng ban</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              <div className="filter-section">
                <div className="filter-label">Sắp xếp theo:</div>
                <div className="filter-options">
                  <button 
                    className={`filter-btn ${sortField === 'fullName' ? 'active' : ''}`}
                    onClick={() => handleSort('fullName')}
                  >
                    Tên {renderSortIcon('fullName')}
                  </button>
                  <button 
                    className={`filter-btn ${sortField === 'totalDistance' ? 'active' : ''}`}
                    onClick={() => handleSort('totalDistance')}
                  >
                    Quãng đường {renderSortIcon('totalDistance')}
                  </button>
                  <button 
                    className={`filter-btn ${sortField === 'activityCount' ? 'active' : ''}`}
                    onClick={() => handleSort('activityCount')}
                  >
                    Số hoạt động {renderSortIcon('activityCount')}
                  </button>
                  <button 
                    className={`filter-btn ${sortField === 'averagePace' ? 'active' : ''}`}
                    onClick={() => handleSort('averagePace')}
                  >
                    Nhịp độ {renderSortIcon('averagePace')}
                  </button>
                  <button 
                    className={`filter-btn ${sortField === 'lastActivityDate' ? 'active' : ''}`}
                    onClick={() => handleSort('lastActivityDate')}
                  >
                    Hoạt động gần nhất {renderSortIcon('lastActivityDate')}
                  </button>
                </div>
              </div>
              
              <div className="filter-actions">
                <button className="reset-btn" onClick={resetFilters}>
                  <FaTimes /> Xóa bộ lọc
                </button>
              </div>
            </div>
          )}
          
          <div className="search-box">
            <div className="search-form">
              <input
                type="text"
                placeholder="Tìm kiếm thành viên..."
                value={search}
                onChange={handleSearchChange}
                className="search-input"
              />
              <div className="search-actions">
                <FaSearch className="search-icon" title="Tìm kiếm"/>
                <span className="search-divider">|</span>
                <FaRedo className="search-icon" title="Làm mới" onClick={() => {setSearch(''); setDebouncedSearch('');}}/>
              </div>
            </div>
          </div>
          
          <div className="filter-summary">
            {filterType !== 'all' && (
              <div className="active-filter">
                {filterType === 'active' && 'Đã hoạt động'}
                {filterType === 'inactive' && 'Chưa hoạt động'}
                {filterType === 'department' && `Phòng ban: ${selectedDepartment}`}
                <button className="clear-filter" onClick={() => filterType === 'department' ? setSelectedDepartment('') : setFilterType('all')}>
                  ×
                </button>
              </div>
            )}
            <div className="results-count">
              {filteredMembers.length} / {teamStats.memberStats.length} thành viên
            </div>
          </div>
          
          <div className="table-container" style={tableHeight ? { minHeight: `${tableHeight}px` } : undefined}>
            <table className="ranking-table">
              <thead>
                <tr>
                  <th className={sortField === 'fullName' ? 'active-sort' : ''} onClick={() => handleSort('fullName')}>
                    Thành viên {renderSortIcon('fullName')}
                  </th>
                  {filteredMembers.some(member => member.department) && (
                    <th>Phòng ban</th>
                  )}
                  <th className={sortField === 'totalDistance' ? 'active-sort' : ''} onClick={() => handleSort('totalDistance')}>
                    Quãng đường {renderSortIcon('totalDistance')}
                  </th>
                  <th className={sortField === 'activityCount' ? 'active-sort' : ''} onClick={() => handleSort('activityCount')}>
                    Hoạt động {renderSortIcon('activityCount')}
                  </th>
                  <th className={sortField === 'totalMovingTime' ? 'active-sort' : ''} onClick={() => handleSort('totalMovingTime')}>
                    Thời gian chạy {renderSortIcon('totalMovingTime')}
                  </th>
                  <th>Tổng thời gian</th>
                  <th className={sortField === 'averagePace' ? 'active-sort' : ''} onClick={() => handleSort('averagePace')}>
                    Nhịp độ TB {renderSortIcon('averagePace')}
                  </th>
                  <th>Độ cao</th>
                  <th className={sortField === 'lastActivityDate' ? 'active-sort' : ''} onClick={() => handleSort('lastActivityDate')}>
                    Hoạt động gần nhất {renderSortIcon('lastActivityDate')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length > 0 ? filteredMembers.map((member) => (
                  <tr 
                    key={member.userId} 
                    onClick={() => handleOpenUserActivities(member.userId, member.fullName || 'Thành viên')}
                    style={{ cursor: 'pointer' }}
                    className="member-row"
                  >
                    <td>
                      <div className="user-info">
                        {member.avatarUrl ? (
                          <img 
                            src={member.avatarUrl} 
                            alt={member.fullName}
                            className="user-avatar"
                          />
                        ) : (
                          <div className="user-avatar-placeholder">
                            {member.fullName ? member.fullName.charAt(0).toUpperCase() : 'U'}
                          </div>
                        )}
                        <span className="user-name">{member.fullName}</span>
                      </div>
                    </td>
                    {filteredMembers.some(m => m.department) && (
                      <td>{member.department || '--'}</td>
                    )}
                    <td className="distance-cell">{formatDistance(member.totalDistance)}</td>
                    <td className="activity-count-cell">{member.activityCount}</td>
                    <td>{formatTime(member.totalMovingTime)}</td>
                    <td>{formatTime(member.totalElapsedTime)}</td>
                    <td>{member.averagePace ? formatPace(member.averagePace) : '--'}</td>
                    <td>{formatElevation(member.totalElevationGain)}</td>
                    <td>{formatDate(member.lastActivityDate)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={filteredMembers.some(m => m.department) ? 9 : 8} style={{ textAlign: 'center', color: '#888' }}>
                      Không tìm thấy thành viên phù hợp
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedUserId && (
        <UserActivitiesDialog
          userId={selectedUserId}
          userName={selectedUserName}
          startDateLocal={startDateLocal}
          endDate={endDate}
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
        />
      )}
    </div>
  );
};

export default TeamStatistics; 